import { Dialog, DialogContent } from '@/components/atoms';
import { formatCurrency } from '@/lib/helpers/format-currency';
import type { ReferralEarningsEntry } from '@/types/referral';
import { cn } from '@/lib/utils';
import { formatReferralDate } from '../utils';

interface EarningsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: ReferralEarningsEntry[];
  totalEarnings: number;
  availableBalance: number;
}

export const EarningsSheet = ({
  open,
  onOpenChange,
  entries,
  totalEarnings,
  availableBalance,
}: EarningsSheetProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
        <div className="flex items-center justify-between gap-3 border-b px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-[#1A1A1A]">
              Earnings history
            </h3>
            <p className="text-sm text-muted-foreground">
              Lifetime {formatCurrency(totalEarnings)} · Available{' '}
              {formatCurrency(availableBalance)}
            </p>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          {entries.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-[#1A1A1A]">
                No earnings yet
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Share your referral link. You&apos;ll see rewards here when
                friends sign up and start selling.
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start justify-between gap-4 border-b border-[#ECEEF1] py-4 last:border-b-0"
                >
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {entry.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatReferralDate(entry.createdAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={cn(
                        'font-semibold',
                        entry.amount >= 0
                          ? 'text-[#18854F]'
                          : 'text-muted-foreground'
                      )}
                    >
                      {formatCurrency(entry.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Balance {formatCurrency(entry.balanceAfter)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
